/**
 * /accounting/reports — Financial Reports hub.
 *
 * Surfaces P&L, Balance Sheet, Cash Flow over the live GL.
 */
import { Link } from "react-router-dom";
import { TrendingUp, Scale, Wallet, Layers, ArrowLeft } from "lucide-react";
import { CockpitPage, CockpitSection } from "@/components/cockpit/CockpitShell";
import { useGeneralLedger } from "@/data/accounting/GeneralLedgerContext";
import { useSeedGeneralLedger } from "@/data/accounting/useSeedGeneralLedger";
import { BooksNavRail } from "@/components/accounting/BooksNavRail";
import {
  computeIncomeStatement, computeBalanceSheet, computeCashFlow,
  listAvailablePeriods, formatPeriodLabel,
} from "@/data/accounting/reports/financialReports";
import { getWipAgingSummary } from "@/data/accounting/reports/wipAging";

const fmt = (n: number) => `${n < 0 ? "-" : ""}$${Math.abs(Math.round(n)).toLocaleString()}`;

export default function FinancialReportsHubPage() {
  useSeedGeneralLedger();
  const { accounts, journalEntries, periodLocks } = useGeneralLedger();
  const periods = listAvailablePeriods(journalEntries);
  const period = periods[0] ?? "all";
  const closedPeriods = new Set(periodLocks.filter(p => p.status === "closed").map(p => p.period));

  const pl = computeIncomeStatement(accounts, journalEntries, period);
  const bs = computeBalanceSheet(accounts, journalEntries, period, closedPeriods);
  const cf = computeCashFlow(accounts, journalEntries, period);
  const wip = getWipAgingSummary(journalEntries);

  const tiles = [
    {
      to: "/accounting/reports/income-statement",
      icon: TrendingUp,
      title: "Income Statement",
      sub: "Revenue, COGS, opex — by trade",
      metric: fmt(pl.netIncome),
      metricLabel: "Net income",
    },
    {
      to: "/accounting/reports/balance-sheet",
      icon: Scale,
      title: "Balance Sheet",
      sub: "Assets, liabilities, equity at period end",
      metric: bs.inBalance ? fmt(bs.assets.total) : "Out of balance",
      metricLabel: "Total assets",
    },
    {
      to: "/accounting/reports/cash-flow",
      icon: Wallet,
      title: "Cash Flow",
      sub: "Direct method · live from cash accounts",
      metric: fmt(cf.netCashChange),
      metricLabel: "Net change",
    },
    {
      to: "/accounting/reports/wip-aging",
      icon: Layers,
      title: "WIP Aging",
      sub: "Open job cost capitalization, bucketed by age",
      metric: fmt(wip.totalOpenBalance),
      metricLabel: `${wip.jobCount} open · ${fmt(wip.staleBalance)} >90d`,
    },
  ];

  return (
    <CockpitPage>
      <BooksNavRail />
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <Link to="/accounting/gl" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-2">
            <ArrowLeft className="h-3 w-3" /> Back to General Ledger
          </Link>
          <h1 className="text-2xl font-semibold tracking-tight">Financial Reports</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Live from the book of record. Period: <span className="font-medium text-foreground">{formatPeriodLabel(period)}</span>.
          </p>
        </div>
      </div>

      <CockpitSection>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {tiles.map(t => (
            <Link
              key={t.to}
              to={t.to}
              className="rounded-md border border-border bg-card p-4 hover:bg-accent/40 transition-colors"
            >
              <div className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-sm bg-muted flex items-center justify-center shrink-0">
                  <t.icon className="h-4 w-4" />
                </div>
                <div className="flex-1">
                  <div className="font-medium">{t.title}</div>
                  <p className="text-xs text-muted-foreground mt-1">{t.sub}</p>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-border">
                <div className="text-xs text-muted-foreground">{t.metricLabel}</div>
                <div className="font-semibold tabular-nums">{t.metric}</div>
              </div>
            </Link>
          ))}
        </div>
      </CockpitSection>
    </CockpitPage>
  );
}
